import { useEffect, useMemo, useState } from 'react';
import { loadClients } from '../services/ClientService';
import { loadClientInvoices } from '../services/ClientInvoiceService';
import { loadClientQuotes } from '../services/ClientQuoteService';
import { loadAccountVendors } from '../services/AccountVendorService';
import { fetchPeople } from '../services/PeopleService';
import { EMPTY_ACCOUNT_SEARCH_DATA, type AccountSearchData } from '../utils/accountSearch';
import type { Workspace } from '../utils/workspaces';

type Loaded = Omit<AccountSearchData, 'projects'>;
const NOTHING_LOADED: Loaded = { clients: [], invoices: [], quotes: [], vendors: [], people: [] };

export interface AccountSearchStatus {
  /** The lists are still being fetched. */
  loading: boolean;
  /** At least one list failed (no permission, network), so results may be missing. */
  failed: boolean;
}

interface State extends AccountSearchStatus { loaded: Loaded }
const INITIAL: State = { loaded: NOTHING_LOADED, loading: true, failed: false };

/**
 * The account-level search index: active clients, invoices, quotes, vendors
 * and (for admins) people, fetched when a search starts rather than on every
 * page load. Projects come from the workspace list already in memory. Each
 * list is fetched on its own, so one failing endpoint only drops its own
 * results, and `failed` says so instead of passing it off as "nothing found".
 */
export function useAccountSearchData(opts: {
  /** True while a search is in progress on an account screen. */
  active: boolean;
  projects: Workspace[];
  isAdmin: boolean;
}): { data: AccountSearchData; status: AccountSearchStatus } {
  const { active, projects, isAdmin } = opts;
  const [state, setState] = useState<State>(INITIAL);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void Promise.allSettled([
      loadClients('', 'active'),
      loadClientInvoices({ archived: 'active' }),
      loadClientQuotes({ archived: 'active' }),
      loadAccountVendors(false),
      // People are for admins only: not even requested for anyone else.
      isAdmin ? fetchPeople('active') : Promise.resolve([]),
    ]).then(settled => {
      if (cancelled) return;
      const list = <T,>(i: number): T[] => (settled[i].status === 'fulfilled' ? (settled[i] as PromiseFulfilledResult<T[]>).value : []);
      setState({
        loaded: { clients: list(0), invoices: list(1), quotes: list(2), vendors: list(3), people: list(4) },
        loading: false,
        failed: settled.some(s => s.status === 'rejected'),
      });
    });
    return () => { cancelled = true; setState(INITIAL); };
  }, [active, isAdmin]);

  // Stable between renders, or the search effect that depends on it would loop.
  const data = useMemo(
    () => (active ? { ...state.loaded, projects } : EMPTY_ACCOUNT_SEARCH_DATA),
    [active, state.loaded, projects],
  );
  const status = useMemo(
    () => ({ loading: active && state.loading, failed: active && state.failed }),
    [active, state.loading, state.failed],
  );
  return { data, status };
}
